export interface ChannelCouponParams {
  source: string;
  medium: string;
  campaign: string;
  forward_domains: string;
}

export interface ChannelCouponContext {
  scene?: number | null;
  strategyId?: number | null;
  canAutoReceive?: boolean;
  source?: string;
  medium?: string;
  campaign?: string;
  forwardDomains?: string;
  received?: boolean;
  receiving?: boolean;
}

export interface ChannelCouponResourceInfo {
  scene?: number | null;
  strategy_id?: number | null;
  can_auto_receive?: boolean;
}

export type ReceiveChannelCouponPayload = {
  strategy_id: number;
  source: string;
  medium?: string;
  campaign?: string;
  forward_domains?: string;
};

type SearchParamReader = Pick<URLSearchParams, 'get'>;

const VISIT_CHANNEL_COUPON_CONTEXT_STORAGE_KEY = 'zl-visit-channel-coupon-context';
const VISIT_CHANNEL_COUPON_RECEIVED_STORAGE_PREFIX = 'zl-visit-channel-coupon-received';

export const buildChannelCouponParams = (
  searchParams: SearchParamReader,
  forwardDomains = '',
): ChannelCouponParams => {
  return {
    source: searchParams.get('utm_source') || '',
    medium: searchParams.get('utm_medium') || '',
    campaign: searchParams.get('utm_campaign') || '',
    forward_domains: forwardDomains,
  };
};

export const hasChannelCouponParams = (params: ChannelCouponParams) => {
  return Boolean(params.source || params.medium || params.campaign || params.forward_domains);
};

export const buildChannelCouponContext = (
  channelCoupon: ChannelCouponResourceInfo | null | undefined,
  params: ChannelCouponParams,
): ChannelCouponContext | undefined => {
  if (!channelCoupon) return undefined;

  return {
    scene: channelCoupon.scene ?? null,
    strategyId: channelCoupon.strategy_id ?? null,
    canAutoReceive: Boolean(channelCoupon.can_auto_receive),
    source: params.source,
    medium: params.medium,
    campaign: params.campaign,
    forwardDomains: params.forward_domains,
    received: false,
    receiving: false,
  };
};

export const isVisitChannelCouponContext = (
  context?: ChannelCouponContext | null,
): context is ChannelCouponContext & { strategyId: number } => {
  return Boolean(context?.scene === 2 && context.canAutoReceive && context.strategyId);
};

export const buildReceiveChannelCouponPayload = (
  context: ChannelCouponContext & { strategyId: number },
): ReceiveChannelCouponPayload => {
  return {
    strategy_id: context.strategyId,
    source: context.source || '',
    medium: context.medium || '',
    campaign: context.campaign || '',
    forward_domains: context.forwardDomains || '',
  };
};

export const appendVisitChannelCouponIdentity = <T extends ChannelCouponParams>(
  params: T,
  context?: ChannelCouponContext | null,
): T & { scene?: number; strategy_id?: number } => {
  if (!isVisitChannelCouponContext(context)) return params;

  return {
    ...params,
    scene: 2,
    strategy_id: context.strategyId,
  };
};

const getSessionStorage = () => {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage;
};

export const getVisitChannelCouponDedupKey = (
  context: ChannelCouponContext,
  customerId?: string | number,
) => {
  const segments = [
    customerId || '',
    context.strategyId || '',
    context.source || '',
    context.medium || '',
    context.campaign || '',
    context.forwardDomains || '',
  ];
  return `${VISIT_CHANNEL_COUPON_RECEIVED_STORAGE_PREFIX}:${segments
    .map((item) => encodeURIComponent(String(item)))
    .join(':')}`;
};

export const hasVisitChannelCouponReceived = (
  context: ChannelCouponContext,
  customerId?: string | number,
) => {
  const storage = getSessionStorage();
  if (!storage) return false;
  return storage.getItem(getVisitChannelCouponDedupKey(context, customerId)) === '1';
};

export const markVisitChannelCouponReceived = (
  context: ChannelCouponContext,
  customerId?: string | number,
) => {
  const storage = getSessionStorage();
  if (!storage) return;
  storage.setItem(getVisitChannelCouponDedupKey(context, customerId), '1');
};

export const saveVisitChannelCouponContext = (context?: ChannelCouponContext | null) => {
  if (!isVisitChannelCouponContext(context)) return;
  const storage = getSessionStorage();
  if (!storage) return;
  storage.setItem(VISIT_CHANNEL_COUPON_CONTEXT_STORAGE_KEY, JSON.stringify(context));
};

export const readVisitChannelCouponContext = (): ChannelCouponContext | undefined => {
  const storage = getSessionStorage();
  if (!storage) return undefined;

  const raw = storage.getItem(VISIT_CHANNEL_COUPON_CONTEXT_STORAGE_KEY);
  if (!raw) return undefined;

  try {
    const parsed = JSON.parse(raw) as ChannelCouponContext;
    return isVisitChannelCouponContext(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

export const clearVisitChannelCouponContext = () => {
  const storage = getSessionStorage();
  if (!storage) return;
  storage.removeItem(VISIT_CHANNEL_COUPON_CONTEXT_STORAGE_KEY);
};
